<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-shield" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">HMOs / TPAs</h4>
              <small style="opacity: .8;">
                {{ total.toLocaleString() }} registered organisations
              </small>
            </div>
            <div class="ml-auto" v-if="canManage">
              <button type="button" class="btn btn-light btn-sm font-weight-bold px-4"
                @click="show = !show">
                <i class="fe mr-1" :class="show ? 'fe-minus' : 'fe-plus'"></i>
                {{ show ? 'Cancel' : 'Add TPA' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Add TPA Form (agency admins) ────────────────────── -->
        <div v-if="canManage" v-show="show" class="card form-card shadow mb-4">
          <div class="form-card-header">
            <div class="section-number">1</div>
            <h6 class="mb-0 font-weight-bold text-white">Register New TPA / HMO</h6>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="registerUser">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="tpa-firstname" class="small font-weight-bold text-secondary">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-firstname" type="text" required class="form-control"
                    placeholder="Contact first name" v-model="register.firstname" />
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-lastname" class="small font-weight-bold text-secondary">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-lastname" type="text" required class="form-control"
                    placeholder="Contact last name" v-model="register.lastname" />
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-organization-name" class="small font-weight-bold text-secondary">
                    Organisation Name <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-organization-name" type="text" required class="form-control"
                    placeholder="e.g. Hygeia HMO" v-model="register.organization_name" />
                </div>
                <div class="form-group col-md-6">
                  <label for="tpa-phone-number" class="small font-weight-bold text-secondary">
                    Phone Number <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-phone-number" v-phone type="text" required class="form-control"
                    placeholder="e.g. 08012345678" v-model="register.phone_number" />
                </div>
                <div class="form-group col-md-4">
                  <label for="tpa-email" class="small font-weight-bold text-secondary">
                    Email <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-email" type="email" required class="form-control"
                    placeholder="email@example.com" v-model="register.email" />
                </div>
                <div class="form-group col-md-4">
                  <label for="tpa-password" class="small font-weight-bold text-secondary">
                    Password <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-password" type="password" required class="form-control"
                    placeholder="Password" v-model="register.password" />
                </div>
                <div class="form-group col-md-4">
                  <label for="tpa-password-confirmation" class="small font-weight-bold text-secondary">
                    Confirm Password <span class="text-danger">*</span>
                  </label>
                  <input id="tpa-password-confirmation" type="password" required class="form-control"
                    placeholder="Repeat password" v-model="register.password_confirmation" />
                </div>
                <div class="form-group col-md-4">
                  <label for="tpa-code" class="small font-weight-bold text-secondary">HMO Code</label>
                  <input id="tpa-code" type="text" maxlength="191" class="form-control"
                    placeholder="e.g. HYG-01" v-model.trim="register.tpa_id" />
                  <small class="form-text text-muted">Printed on enrollees' ID cards.</small>
                </div>
                <div class="form-group col-md-8">
                  <label for="tpa-address" class="small font-weight-bold text-secondary">Office Address</label>
                  <input id="tpa-address" type="text" maxlength="1000" class="form-control"
                    placeholder="e.g. 12 Herbert Macaulay Way, Wuse, Abuja" v-model.trim="register.address" />
                </div>
              </div>

              <div class="d-flex justify-content-end" style="gap: 10px;">
                <button type="button" class="btn btn-outline-secondary px-4"
                  @click="show = false; clearIt()">
                  Cancel
                </button>
                <button type="submit" class="btn btn-submit px-5">
                  <i class="fe fe-send mr-2"></i> Register TPA
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- ── Stats / Export Controls ────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4">
            <div class="d-flex flex-wrap align-items-end" style="gap: 12px;">
              <div style="flex: 1; min-width: 200px;">
                <label for="tpa-stats-programmes" class="small font-weight-bold text-secondary mb-1">Filter by Programme</label>
                <v-select input-id="tpa-stats-programmes" v-model="form.sectors" multiple :options="sectors"
                  :reduce="name => name.name" label="name" placeholder="Select programmes…" />
              </div>
              <button class="btn btn-sm btn-outline-primary" @click="getStats">
                <i class="fe fe-bar-chart-2 mr-1"></i> Get Statistics
              </button>
              <button class="btn btn-sm btn-outline-success">
                <download-excel :data="stats" :fields="json_fields" type="xls"
                  :escapeCsv="false" :name="`HMOs`">
                  <i class="fe fe-download mr-1"></i> Export Excel
                </download-excel>
              </button>
            </div>
          </div>
        </div>

        <!-- ── TPA Table ───────────────────────────────────────── -->
        <div class="card table-card shadow">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <div>
              <h6 class="mb-0 font-weight-bold text-dark">
                <i class="fe fe-list text-primary mr-2"></i> Registered TPAs / HMOs
              </h6>
              <small class="text-muted">
                {{ shownTpas.length }} shown{{ meta.last_page > 1 ? ' on this page' : '' }}
              </small>
            </div>
            <input type="search" class="form-control form-control-sm tpa-search" v-model.trim="search"
              aria-label="Search HMOs by name, code, email or phone" placeholder="Search name, code, email or phone…" />
          </div>

          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th px-4">Organisation</th>
                  <th class="table-th">Phone Number</th>
                  <th class="table-th">Status</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tpa in shownTpas" :key="tpa.id" class="table-row">
                  <td class="px-4 py-3">
                    <div class="d-flex align-items-center" style="gap: 10px;">
                      <div class="employee-avatar">
                        {{ (tpa.organization_name || '?')[0].toUpperCase() }}
                      </div>
                      <div>
                        <p class="mb-0 font-weight-bold" style="font-size: .88rem;">
                          {{ tpa.organization_name }}
                        </p>
                        <small class="text-muted">{{ tpa.email }}</small>
                        <span v-if="tpa.tpa_id" class="rel-chip ml-1">{{ tpa.tpa_id }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="py-3" style="font-size: .88rem;">{{ tpa.phone_number }}</td>
                  <td class="py-3">
                    <span class="status-badge" :class="isActive(tpa) ? 'status-badge--approved' : 'status-badge--pending'">
                      {{ isActive(tpa) ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="py-3">
                    <div class="d-flex" style="gap: 6px;">
                      <router-link :to="'/tpa/' + tpa.id" class="btn btn-sm btn-outline-primary">
                        <i class="fe fe-eye mr-1"></i> View
                      </router-link>
                      <router-link v-if="canManage" :to="'/edit-tpa/' + tpa.id" class="btn btn-sm btn-outline-secondary">
                        <i class="fe fe-edit-2 mr-1"></i> Edit
                      </router-link>
                    </div>
                  </td>
                </tr>
                <tr v-if="loadingList">
                  <td colspan="4" class="text-center text-muted py-5">Loading…</td>
                </tr>
                <tr v-else-if="!shownTpas.length">
                  <td colspan="4" class="text-center text-muted py-5">
                    <i class="fe fe-shield" style="font-size: 2rem; display: block; margin-bottom: 8px;"></i>
                    {{ search ? 'No HMO matches your search' : 'No TPAs registered yet' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="meta.last_page > 1" class="d-flex align-items-center justify-content-between px-4 py-3 border-top">
            <small class="text-muted">Page {{ meta.current_page }} of {{ meta.last_page }}</small>
            <div class="d-flex" style="gap: 8px;">
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page <= 1 || loadingList"
                @click="getTPAs(meta.current_page - 1)"><i class="fe fe-chevron-left"></i> Previous</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page || loadingList"
                @click="getTPAs(meta.current_page + 1)">Next <i class="fe fe-chevron-right"></i></button>
            </div>
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import sectorsJson from "@/json/sectors_dynamic.json";

  export default {
    components: {
      Navbar,
      Loading,
    },
    data() {
      return {
        agency: JSON.parse(localStorage.getItem("agency")) || {},
        user: JSON.parse(localStorage.getItem("user")),
        stats: "",
        providers: "",
        agencies: "",
        edit: false,
        show: false,
        isLoading: false,
        fullPage: true,
        sectors: ["Voluntary Contributor", "State Civil Servant", "T-SHIP"],
        tpas: [],
        meta: { total: 0, current_page: 1, last_page: 1 },
        loadingList: false,
        search: "",
        form: {
          sectors: ["Voluntary Contributor", "State Civil Servant", "T-SHIP"],
        },
        json_fields: {
          "HMO Name": "organization_name",
          "HMO Code": "tpa_id",
          "Total Principal ": "enrollees_count",
          "Male Total": "male_count",
          "female Total": "female_count",
          "Dependent Count": "dependants_count",
          Total: "total",
        },
        register: {
          firstname: "",
          lastname: "",
          organization_name: "",
          tpa_id: "",
          address: "",
          email: "",
          phone_number: "",
          state: "",
          agency_name: "",
          ward: "",
          services_offered: "",
          localgovt: "",
          address1: "",
          role: 0,
          password: "",
          password_confirmation: "",
        },
      };
    },
    computed: {
      // Agency admins (the agency account, or staff with user_role 1) add and edit HMOs.
      canManage() {
        return !!this.user && (this.user.type === "shis" || Number(this.user.user_role) === 1);
      },
      total() {
        return Number(this.meta.total) || this.tpas.length;
      },
      shownTpas() {
        const q = this.search.toLowerCase();
        if (!q) return this.tpas;
        return this.tpas.filter((t) =>
          [t.organization_name, t.email, t.phone_number, t.tpa_id].some((v) => String(v || "").toLowerCase().includes(q)));
      },
    },
    mounted() {
      let sectors = sectorsJson;
      let formatter = sectors.filter((x) => x.agency_id == this.agency.id);
      this.sectors = formatter;
    },
    methods: {
      isActive(tpa) {
        return tpa.org_status === null || tpa.org_status === undefined || Number(tpa.org_status) === 1;
      },

      registerUser() {
        const agency = this.agency;
        const code = (this.register.tpa_id || "").toLowerCase();
        if (code && this.tpas.some((t) => String(t.tpa_id || "").trim().toLowerCase() === code)) {
          this.$toasted.error("Another HMO in this agency already has this HMO code.", { position: "top-center", duration: 5000 });
          return;
        }
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/registerProvider", {
            email: this.register.email,
            phone_number: this.register.phone_number,
            agency_name: this.register.agency_name,
            firstname: this.register.firstname,
            lastname: this.register.lastname,
            state: agency.state,
            localgovt: agency.localgovt,
            type: "tpa",
            password: this.register.password,
            password_confirmation: this.register.password_confirmation,
            enrolled_by: 0,
          })
          .then((response) => {
            let tpa_user_id = response.data.data.id;
            this.show = false;
            console.log(response);
            this.axios
              .post("/api/v1/auth/orgenrollment", {
                user_id: tpa_user_id,
                email: this.register.email,
                phone_number: this.register.phone_number,
                organization_name: this.register.organization_name,
                tpa_id: this.register.tpa_id || null,
                address: this.register.address || null,
                agency_id: agency.id,
              })
              .then(() => {
                this.getTPAs(1);
                this.clearIt();
                this.$toasted.success("Organisation added successfully!", {
                  position: "top-center",
                  duration: 4000,
                });
              })
              .catch((error) => {
                console.log(error.response);
                this.$toasted.error("The sign-in was created but the organisation was not saved", {
                  position: "top-center",
                  duration: 5000,
                });
              })
              .finally(() => {
                this.isLoading = false;
              });
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            this.$toasted.error("Registration not successful", {
              position: "top-center",
              duration: 4000,
            });
          });
      },

      getTPAs(page = 1) {
        this.loadingList = true;
        this.axios
          .get(`/api/v1/auth/org_agency/${this.agency.id}`, { params: { page } })
          .then((response) => {
            this.tpas = response.data.data || [];
            this.meta = Object.assign({ total: 0, current_page: 1, last_page: 1 }, response.data.meta);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.loadingList = false;
          });
      },

      clearIt() {
        this.register.firstname = "";
        this.register.lastname = "";
        this.register.email = "";
        this.register.phone_number = "";
        this.register.organization_name = "";
        this.register.tpa_id = "";
        this.register.address = "";
        this.register.password = "";
        this.register.password_confirmation = "";
      },

      getStats() {
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/hmo/stats`, {
            params: {
              agency_id: this.agency.id,
              sectors: this.form.sectors,
            },
          })
          .then((response) => {
            this.stats = response.data.data;
            this.$toasted.success("Statistics fetched!", {
              position: "top-center",
              duration: 3000,
            });
            this.isLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;
          });
      },
    },
    created() {
      this.getTPAs();
    },
  };
</script>

<style scoped>
.tpa-search {
  max-width: 260px;
}
</style>
